/**
 * Canonical file-extension and asset-category definitions.
 *
 * Every extension list in the codebase should derive from these constants
 * rather than maintaining its own copy.
 */

// ── Asset categories ──────────────────────────────────────────────────

/** Extensions for 3D model formats. */
export const MODEL_EXTENSIONS = new Set(['glb', 'gltf']);

/** Extensions for Gaussian splat formats (subset of models with special loading). */
export const SPLAT_EXTENSIONS = new Set(['spz', 'ply', 'splat']);

/** All 3D asset extensions (models + splats). */
export const ALL_3D_EXTENSIONS = new Set([...MODEL_EXTENSIONS, ...SPLAT_EXTENSIONS]);

/** Extensions for image formats that browsers can display natively. */
export const NATIVE_IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'webp']);

/** Extensions for image formats requiring custom decoding (GeoTIFF). */
export const DECODED_IMAGE_EXTENSIONS = new Set(['tif', 'tiff']);

/** All image extensions (native + decoded). */
export const IMAGE_EXTENSIONS = new Set([...NATIVE_IMAGE_EXTENSIONS, ...DECODED_IMAGE_EXTENSIONS]);

/** Extensions for video formats. */
export const VIDEO_EXTENSIONS = new Set(['mp4', 'webm']);

/**
 * Extensions for audio formats. Decoded by the browser's HTMLAudioElement.
 * `opus` is Ogg Opus (RFC 7845) — the recording voice format (RECORDINGS_SPEC §5).
 */
export const AUDIO_EXTENSIONS = new Set(['mp3', 'wav', 'ogg', 'opus']);

/** Extensions for scene/project bundles. */
export const SCENE_EXTENSIONS = new Set(['trace', 'tracex', 'zip', 'glxf']);

/** Extensions for script files. */
export const SCRIPT_EXTENSIONS = new Set(['ts']);

/** Extensions for data files. */
export const DATA_EXTENSIONS = new Set(['json']);

// ── Derived sets ──────────────────────────────────────────────────────

/**
 * All single-asset extensions that can be dropped into a scene.
 * Does NOT include scene bundles (.trace, .zip) which go through a different load path.
 */
export const DROPPABLE_ASSET_EXTENSIONS = new Set([
    ...ALL_3D_EXTENSIONS,
    ...IMAGE_EXTENSIONS,
    ...VIDEO_EXTENSIONS,
    ...AUDIO_EXTENSIONS,
]);

// ── Category lookup ───────────────────────────────────────────────────

export type AssetCategory = 'model' | 'splat' | 'image' | 'video' | 'audio' | 'scene' | 'script' | 'data' | 'unknown';

/** Get the asset category for a file extension (without leading dot). */
export function getAssetCategory(ext: string): AssetCategory {
    const lower = ext.toLowerCase();
    if (SPLAT_EXTENSIONS.has(lower)) return 'splat';
    if (MODEL_EXTENSIONS.has(lower)) return 'model';
    if (IMAGE_EXTENSIONS.has(lower)) return 'image';
    if (VIDEO_EXTENSIONS.has(lower)) return 'video';
    if (AUDIO_EXTENSIONS.has(lower)) return 'audio';
    if (SCENE_EXTENSIONS.has(lower)) return 'scene';
    if (SCRIPT_EXTENSIONS.has(lower)) return 'script';
    if (DATA_EXTENSIONS.has(lower)) return 'data';
    return 'unknown';
}

// ── MIME types ────────────────────────────────────────────────────────
// Includes formats not in the extension sets above (mov, gif) used by
// Utility.mimeTypeFromFilename() — Safari requires correct MIME on blob URLs.

const MIME_MAP: Record<string, string> = {
    // Video
    mp4: 'video/mp4',
    webm: 'video/webm',
    mov: 'video/quicktime',
    // Audio
    mp3: 'audio/mpeg',
    wav: 'audio/wav',
    ogg: 'audio/ogg',
    // Ogg Opus: the container is Ogg, so the registered type is audio/ogg
    // (RFC 7845 §9), which is also what a 2.1 `files[]` entry declares.
    opus: 'audio/ogg',
    // Image
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
    gif: 'image/gif',
    tif: 'image/tiff',
    tiff: 'image/tiff',
    // 3D
    glb: 'model/gltf-binary',
    // The REGISTERED type. It is what a 2.1 `files[]` entry must declare for a
    // document, and what the manifest classifier and the reader's
    // "is this a nested scene document" test key on — an entry typed
    // `application/gltf+json` classifies as neither model nor document and
    // silently loses its sub-scene (spec §3.4, plan §1).
    gltf: 'model/gltf+json',
    // Data: a 2.1 `files[]` entry declared as application/json.
    json: 'application/json',
};

/**
 * The extension for a MIME type, or undefined when it names nothing known.
 * Parameters are ignored. Where several extensions share a type (jpg/jpeg), the
 * first listed in MIME_MAP wins.
 */
export function extensionFromMimeType(mimeType: string | undefined): string | undefined {
    const essence = mimeType?.split(';')[0].trim().toLowerCase();
    if (!essence) return undefined;
    return Object.keys(MIME_MAP).find(ext => MIME_MAP[ext] === essence);
}

// ── Streamable media (spec §3.3) ──────────────────────────────────────

/**
 * Streaming MIME types, a subset of external media allowed by §3.3.
 * See {@link isExternalMediaMimeType} for manifest reference policy.
 *
 * `video/` and `audio/` match as PREFIXES (see {@link isStreamableMimeType});
 * the two manifest types below are exact, because an HLS or DASH playlist is
 * the entry point to a stream rather than media itself.
 *
 * Mirrors `ContentConstants.StreamableMimeTypes` in TraceBackend — the client
 * pre-flight and the server gate must classify identically (spec §2.5).
 */
export const STREAMABLE_MIME_TYPES: ReadonlySet<string> = new Set([
    'application/vnd.apple.mpegurl',
    'application/dash+xml',
]);

/** Mime prefixes that make an entry streamable whatever its subtype. */
const STREAMABLE_MIME_PREFIXES = ['video/', 'audio/'] as const;

/**
 * Whether a mime may be referenced from a foreign host.
 *
 * Parameters are ignored (`video/mp4; codecs=avc1` is `video/mp4`); a missing
 * or blank mime is never streamable, because the check is on what the entry
 * CLAIMS and an entry that claims nothing has not earned the exemption.
 */
export function isStreamableMimeType(mimeType: string | undefined): boolean {
    if (!mimeType) return false;
    const essence = mimeType.split(';')[0].trim().toLowerCase();
    if (!essence) return false;
    return STREAMABLE_MIME_TYPES.has(essence)
        || STREAMABLE_MIME_PREFIXES.some(prefix => essence.startsWith(prefix));
}

/**
 * Foreign GLBs and images may remain references alongside streams (§3.3).
 * Mirrors ContentConstants.IsExternalMediaMimeType in TraceBackend.
 * This is a MIME rule only; storage ownership and library access still apply.
 * JSON documents, archives and untyped binaries are outside this leaf-media rule.
 */
export function isExternalMediaMimeType(mimeType: string | undefined): boolean {
    if (!mimeType) return false;
    const essence = mimeType.split(';')[0].trim().toLowerCase();
    return essence === 'model/gltf-binary'
        || (essence.startsWith('image/') && essence.length > 'image/'.length)
        || isStreamableMimeType(mimeType);
}

/** Infer a MIME type from a file extension (without leading dot). Returns undefined for unknown types. */
export function mimeTypeFromExtension(ext: string): string | undefined {
    return MIME_MAP[ext.toLowerCase()];
}

// ── Helpers ───────────────────────────────────────────────────────────

/** Returns true if the extension is a GeoTIFF/TIFF that requires custom decoding. */
export function isDecodedImageExtension(ext: string): boolean {
    return DECODED_IMAGE_EXTENSIONS.has(ext.toLowerCase());
}

/** Build a dotted extension array (e.g. ['.jpg', '.png']) from a Set of bare extensions. */
export function toDotExtensions(exts: Set<string>): string[] {
    return [...exts].map(e => `.${e}`);
}

/**
 * Build an HTML accept string from extension sets (e.g. "image/jpeg,image/png,.tif,.tiff").
 * A mime shared by several extensions (ogg + opus → audio/ogg) is listed once,
 * and the extensions that share it are listed explicitly as well — a file
 * picker's mime → extension table need not cover every one of them.
 */
export function toAcceptString(exts: Set<string>): string {
    const mimeCounts = new Map<string, number>();
    for (const [, mime] of Object.entries(MIME_MAP)) mimeCounts.set(mime, (mimeCounts.get(mime) ?? 0) + 1);
    const out = new Set<string>();
    for (const e of exts) {
        const mime = mimeTypeFromExtension(e);
        if (!mime) { out.add(`.${e}`); continue; }
        out.add(mime);
        if ((mimeCounts.get(mime) ?? 0) > 1) out.add(`.${e}`);
    }
    return [...out].join(',');
}

/**
 * Should this image source enable alpha blending on its material?
 *
 * Deliberately PNG-only: webp/gif CAN carry alpha but are usually opaque,
 * and blanket-enabling ALPHABLEND moves the mesh into the transparent
 * rendering group — a sorting regression for every opaque webp. Widening
 * this needs a transparency-sorting sanity check first (#660).
 *
 * Recognized forms:
 * - a path/URL whose filename ends in .png (case-insensitive), with query
 *   strings and fragments stripped ("foo.png?v=2", "foo.PNG#x");
 * - a PNG data URL, strictly `data:image/png` terminated by `;` or `,`
 *   (never a hypothetical `data:image/pngx`).
 *
 * An extension-less URL (a library reference, `<api>/assets/{guid}/file`)
 * cannot be judged from the string alone. Where the bytes are in hand,
 * {@link isPngBlob} answers.
 */
export function isAlphaCapableImageSrc(src: string): boolean {
    const lower = src.toLowerCase();
    if (lower.startsWith('data:')) {
        return /^data:image\/png[;,]/.test(lower);
    }
    // Strip query/fragment before testing the extension.
    const path = lower.split(/[?#]/, 1)[0];
    return path.endsWith('.png');
}

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

/**
 * Whether these bytes open with the PNG signature, a constant the PNG spec
 * fixes, so the answer never depends on how the file is named. A blob that
 * cannot be read is simply not a PNG, so the caller keeps the src's verdict.
 */
export async function isPngBlob(blob: Blob): Promise<boolean> {
    try {
        const head = new Uint8Array(await blob.slice(0, PNG_SIGNATURE.length).arrayBuffer());
        return head.length === PNG_SIGNATURE.length && PNG_SIGNATURE.every((byte, i) => head[i] === byte);
    } catch {
        return false;
    }
}

/** `glTF`, the first four bytes of every binary glTF. */
export const GLB_MAGIC = [0x67, 0x6c, 0x54, 0x46];

/**
 * Whether these bytes open with the GLB magic, whatever the blob was served as
 * or named. A blob that cannot be read is simply not a GLB.
 */
export async function isGlbBlob(blob: Blob): Promise<boolean> {
    try {
        const head = new Uint8Array(await blob.slice(0, GLB_MAGIC.length).arrayBuffer());
        return head.length === GLB_MAGIC.length && GLB_MAGIC.every((byte, i) => head[i] === byte);
    } catch {
        return false;
    }
}

/** Paths whose asset genuinely IS markup, so a page body is the right answer. */
const MARKUP_ASSET_PATH = /\.(?:html?|xhtml|svg)$/i;

/** `<!doctype html>`, `<html …>`, or an XHTML preamble, with or without a BOM. */
const HTML_DOCUMENT_HEAD = /^\uFEFF?\s*(?:<!doctype\s+html|<html[\s>]|<\?xml[^>]*\?>\s*(?:<!doctype\s+html|<html[\s>]))/i;

/** Markup of any kind: what a page that does not open with a doctype starts with. */
const MARKUP_HEAD = /^\uFEFF?\s*</;

/**
 * Whether a download that succeeded is a web page instead of the asset asked
 * for, and so must not be bundled under the asset's name.
 *
 * A dev server that cannot resolve a path does not 404 — it serves its SPA
 * fallback with `200 text/html`. So a `src` pointing at a file that is not
 * there (a moved CDN path behind a friendly error page, a placeholder path an
 * authoring tool never resolved) downloads cleanly, and the archive would get
 * a file with the right name and `<!DOCTYPE html>` inside it. Nothing would
 * complain until a viewer somewhere tried to parse it as GLB, by which time
 * the archive has been published.
 *
 * The bytes decide. A GLB opens with its magic and is an asset whatever the
 * server called it (a wrong label is not a reason to refuse a real model); an
 * HTML document head is a page whatever the server called it (a dev server
 * that mislabels its fallback).
 *
 * The content-type is only a hint, for a body the bytes leave open: `text/html`
 * over a body that is markup at all — a fragment, a page that opens with a
 * comment — counts as a page, and over anything else it counts for nothing.
 *
 * A `url` that asks for markup (`.html`, `.svg`) is never a page in this sense.
 */
export function isWebPageNotAsset(url: string, contentType: string | null, body: ArrayBuffer): boolean {
    const path = url.split(/[?#]/)[0];
    if (MARKUP_ASSET_PATH.test(path)) return false;
    return isHtmlDocument(contentType, body);
}

/** Byte-level HTML guard for binary companions, which never intentionally contain a page. */
export function isHtmlDocument(contentType: string | null, body: ArrayBuffer): boolean {
    const bytes = new Uint8Array(body, 0, Math.min(body.byteLength, 256));
    if (GLB_MAGIC.every((byte, i) => bytes[i] === byte)) return false;
    const head = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
    if (HTML_DOCUMENT_HEAD.test(head)) return true;
    return !!contentType && /^\s*text\/html\b/i.test(contentType) && MARKUP_HEAD.test(head);
}
